import Step from "@material-ui/core/Step";
import StepContent from "@material-ui/core/StepContent";
import StepLabel from "@material-ui/core/StepLabel";
import Stepper from "@material-ui/core/Stepper";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import React, {
  ComponentClass,
  ComponentProps,
  FunctionComponent,
  useContext,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { size } from "../../styles/media";
import { PrimaryButton } from "../button";
import { Widget } from "../widget";
import { Content, CTAWrapper } from "./common";
import { StepperContext } from "./context";
import { StepperPropTypes } from "./models";

const StyledContent = styled(Content)`
  @media (max-width: ${size.md - 1}px) {
    margin: 0;
  }
`;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
  },
  label: {},
  labelIcon: {},
  button: {
    "&:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  actionsContainer: {
    marginBottom: theme.spacing(2),
  },
  resetContainer: {
    padding: theme.spacing(3),
  },
}));

export function VerticalLinearStepper<
  T extends {
    content:
      | FunctionComponent<A>
      | ComponentClass<A, any>
      | ((x: A) => JSX.Element);
  },
  A extends {} = ComponentProps<T["content"]>
>({
  steps,
  stepsProps,
  finalPage,
  onFinish,
  finishText,
  nextButton,
  backButton,
  className,
}: StepperPropTypes<T, A>) {
  const classes = useStyles();
  const { activeStep, back, next, isNextEnabled, isBackEnabled } = useContext(
    StepperContext
  );
  const { t } = useTranslation("stepper");

  const memodSteps = useMemo(
    () =>
      steps.map((step, index) => (
        <Step key={index}>
          <StepLabel>{step.title}</StepLabel>
          <StepContent>
            <Typography>
              {React.createElement(step.content, stepsProps)}
            </Typography>
            <div className={classes.actionsContainer}>
              <div>
                {activeStep === 0 ? null : backButton ? (
                  React.cloneElement(
                    backButton,
                    {
                      className: `${classes.button} ${backButton.props.className}`,
                      onClick: (e: any) => back.next(e),
                      disabled: activeStep === 0 || !isBackEnabled,
                    },
                    t("back")
                  )
                ) : (
                  <PrimaryButton
                    disabled={activeStep === 0 || !isBackEnabled}
                    onClick={(e) => back.next(e)}
                    className={classes.button}
                  >
                    {t("back")}
                  </PrimaryButton>
                )}

                {nextButton ? (
                  React.cloneElement(
                    nextButton,
                    {
                      className: `${classes.button} ${nextButton.props.className}`,
                      disabled: !isNextEnabled,
                      onClick: (e: any) => next.next(e),
                    },
                    steps[activeStep]?.nextButtonText ||
                      (activeStep === steps.length - 1
                        ? t("finish")
                        : t("next"))
                  )
                ) : (
                  <PrimaryButton
                    disabled={!isNextEnabled}
                    variant="contained"
                    onClick={(e) => next.next(e)}
                    className={classes.button}
                  >
                    {steps[activeStep]?.nextButtonText ||
                      (activeStep === steps.length - 1
                        ? t("finish")
                        : t("next"))}
                  </PrimaryButton>
                )}
              </div>
            </div>
          </StepContent>
        </Step>
      )),
    [
      steps,
      stepsProps,
      classes,
      back,
      next,
      activeStep,
      isBackEnabled,
      isNextEnabled,
    ]
  );

  const footer = useMemo(
    () =>
      activeStep === steps.length ? (
        <>
          <StyledContent>
            {finalPage || (
              <Typography
                dangerouslySetInnerHTML={{ __html: t("all-steps-completed") }}
              ></Typography>
            )}
          </StyledContent>
          {onFinish ? (
            <CTAWrapper>
              {nextButton ? (
                React.cloneElement(
                  nextButton,
                  {
                    onClick: onFinish,
                  },
                  finishText || t("finish")
                )
              ) : (
                <PrimaryButton onClick={onFinish}>
                  {finishText || t("finish")}
                </PrimaryButton>
              )}
            </CTAWrapper>
          ) : null}
        </>
      ) : null,
    [
      activeStep,
      steps.length,
      finalPage,
      t,
      onFinish,
      nextButton,
      onFinish,
      finishText,
    ]
  );

  return (
    <Widget className={className}>
      <Stepper
        activeStep={activeStep}
        orientation="vertical"
        className={classes.root}
      >
        {memodSteps}
      </Stepper>
      {footer}
    </Widget>
  );
}
